import React from "react";

/**
 * A visually rich default hero image for news items without an image_url.
 * Rendered as an inline SVG so it never fails to load and inherits sizing
 * from its parent (like <img>). Uses the NiftyNews brand palette.
 *
 * Two variants are chosen deterministically based on a hash of the seed
 * (usually the article title) so the same article always gets the same look.
 */
const HUES = [
    // (from, via, to, accent)
    { from: "#0F172A", via: "#1E293B", to: "#312E81", accent: "#818CF8" }, // indigo night
    { from: "#0F172A", via: "#1F2937", to: "#831843", accent: "#F472B6" }, // rose night
    { from: "#0F172A", via: "#164E63", to: "#083344", accent: "#22D3EE" }, // teal night
    { from: "#0F172A", via: "#3F1D38", to: "#4C1D95", accent: "#C084FC" }, // violet night
    { from: "#0F172A", via: "#134E4A", to: "#064E3B", accent: "#34D399" }, // emerald night
    { from: "#0F172A", via: "#312E81", to: "#0C4A6E", accent: "#60A5FA" }, // blue night
];

const hash = (s) => {
    let h = 0;
    const str = String(s || "");
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
    return Math.abs(h);
};

const DefaultNewsImage = ({ seed = "", label = "NiftyNews", className = "" }) => {
    const palette = HUES[hash(seed) % HUES.length];
    const gid = `bg-${hash(seed) % 100000}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 800 500"
            preserveAspectRatio="xMidYMid slice"
            className={className}
            style={{ width: "100%", height: "100%", display: "block" }}
            role="img"
            aria-label="Article illustration"
        >
            <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={palette.from} />
                    <stop offset="55%" stopColor={palette.via} />
                    <stop offset="100%" stopColor={palette.to} />
                </linearGradient>
                <radialGradient id={`${gid}-glow`} cx="0.8" cy="0.15" r="0.7">
                    <stop offset="0%" stopColor={palette.accent} stopOpacity="0.4" />
                    <stop offset="100%" stopColor={palette.accent} stopOpacity="0" />
                </radialGradient>
                <pattern id={`${gid}-grid`} width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M40 0 L0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
                </pattern>
            </defs>
            <rect width="800" height="500" fill={`url(#${gid})`} />
            <rect width="800" height="500" fill={`url(#${gid}-grid)`} />
            <rect width="800" height="500" fill={`url(#${gid}-glow)`} />

            {/* Sparkline-ish decorative line */}
            <path
                d="M0 340 L80 300 L160 310 L240 260 L320 280 L400 200 L480 220 L560 160 L640 180 L720 120 L800 140"
                fill="none"
                stroke={palette.accent}
                strokeWidth="2"
                opacity="0.55"
            />
            <path
                d="M0 340 L80 300 L160 310 L240 260 L320 280 L400 200 L480 220 L560 160 L640 180 L720 120 L800 140 L800 500 L0 500 Z"
                fill={palette.accent}
                opacity="0.08"
            />

            {/* Wordmark */}
            <g transform="translate(48 420)">
                <text
                    x="0"
                    y="0"
                    fill="white"
                    style={{ fontFamily: "Fraunces, Georgia, serif", fontWeight: 900, fontSize: 52, fontStyle: "italic", letterSpacing: -1.5 }}
                >
                    Nifty
                    <tspan fill={palette.accent}>News</tspan>
                    <tspan fill="#EF4444">.</tspan>
                </text>
                <text
                    x="4"
                    y="26"
                    fill="rgba(255,255,255,0.55)"
                    style={{ fontFamily: "Space Grotesk, monospace", fontSize: 12, letterSpacing: 3, textTransform: "uppercase" }}
                >
                    {label}
                </text>
            </g>

            {/* Arrow accent (mirrors the logo) */}
            <g transform="translate(680 90)">
                <rect x="0" y="0" width="52" height="52" fill="rgba(255,255,255,0.08)" rx="4" />
                <path d="M15 37 L37 15 M20 15 L37 15 L37 32" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
        </svg>
    );
};

export default DefaultNewsImage;
